import { type VariantProps, cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../../lib';
import { type Tone, filledToneClasses, lightToneClasses } from '../tone';

const themeIconVariants = cva('inline-flex shrink-0 items-center justify-center', {
  variants: {
    size: {
      sm: 'size-6 [&>svg]:size-3.5',
      md: 'size-8 [&>svg]:size-4',
      lg: 'size-10 [&>svg]:size-5',
      xl: 'size-14 [&>svg]:size-7',
    },
    radius: {
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    },
    variant: {
      light: '',
      filled: '',
    },
    tone: {
      primary: '',
      muted: '',
      info: '',
      success: '',
      warning: '',
      destructive: '',
    } satisfies Record<Tone, string>,
  },
  compoundVariants: (Object.keys(lightToneClasses) as Tone[]).flatMap(tone => [
    { variant: 'light' as const, tone, className: lightToneClasses[tone] },
    { variant: 'filled' as const, tone, className: filledToneClasses[tone] },
  ]),
  defaultVariants: {
    size: 'lg',
    radius: 'lg',
    variant: 'light',
    tone: 'primary',
  },
});

type ThemeIconProps = React.ComponentProps<'span'> & VariantProps<typeof themeIconVariants>;

function ThemeIcon({ size, radius, variant, tone, className, ...props }: ThemeIconProps) {
  return (
    <span
      aria-hidden
      className={cn(themeIconVariants({ size, radius, variant, tone }), className)}
      {...props}
    />
  );
}

export { ThemeIcon, themeIconVariants };
